let refinerClientPromise = null;

const REFINER_IDENTIFY_SIGNATURE_KEY = "refiner_identify_signature";

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function compact(value) {
  return Object.fromEntries(
    Object.entries(value).filter(
      ([, item]) => item !== null && item !== undefined && item !== "",
    ),
  );
}

function clearStorageItem(storage, key) {
  try {
    storage?.removeItem(key);
  } catch {
    // Storage can be unavailable in hardened browser modes.
  }
}

function clearRefinerIdentifySignature() {
  if (!isBrowser()) {
    return;
  }

  clearStorageItem(window.localStorage, REFINER_IDENTIFY_SIGNATURE_KEY);
  clearStorageItem(window.sessionStorage, REFINER_IDENTIFY_SIGNATURE_KEY);
  document.cookie = `${REFINER_IDENTIFY_SIGNATURE_KEY}=; Max-Age=0; path=/`;
}

async function getRefinerClient() {
  if (!isBrowser()) {
    return null;
  }

  if (!refinerClientPromise) {
    refinerClientPromise = import("refiner-js")
      .then((module) => module.default ?? module)
      .catch((error) => {
        refinerClientPromise = null;
        throw error;
      });
  }

  return refinerClientPromise;
}

export async function identifyRefinerUser({ projectId, locale, user }) {
  if (!projectId || (!user?.id && !user?.email)) {
    return false;
  }

  const refiner = await getRefinerClient();

  if (!refiner) {
    return false;
  }

  const userTraits = compact(user);

  /*
   * Refiner uses this signature to throttle identical identify payloads.
   * On explicit login / cookie accept / same-user relogin we want identity to
   * be sent again, so clear it before identifyUser.
   */
  clearRefinerIdentifySignature();

  await refiner("setProject", projectId);

  if (import.meta.env.DEV) {
    // await refiner("enableDebugMode");
  }

  /*
   * UI-only; does not send a dismiss event.
   */
  await refiner("closeForm");

  /*
   * Important:
   * If the SDK was previously in anonymous mode, reset it before identifying.
   * This must be before identifyUser, not after.
   */
  await refiner("resetUser");

  /*
   * Refiner recommends setLocale right before identifyUser.
   */
  if (locale) {
    await refiner("setLocale", locale);
  }

  /*
   * Documented way to make the incoming traits replace the existing user traits.
   */
  await refiner("setWriteOperation", "replace");

  /*
   * Important:
   * Keep identifyUser in the simple documented shape:
   * one user object containing id/email/name/traits.
   */
  await refiner("identifyUser", userTraits);

  /*
   * Re-enable after identifyUser.
   */
  await refiner("enableClient");
  await refiner("startSession");

  return true;
}

export async function resetRefinerClient() {
  const refiner = await getRefinerClient();

  clearRefinerIdentifySignature();

  if (!refiner) {
    return false;
  }

  await refiner("closeForm");
  await refiner("resetUser");
  await refiner("disableClient");

  return true;
}
